import {
  ACCESS_TOKEN_REFRESH_SKEW_MS,
  API_HEADERS,
  AUTH_ENDPOINTS,
  CSRF_COOKIE_NAME,
  DEFAULT_REQUEST_TIMEOUT_MS,
  SESSION_SYNC,
  type AuthRealm,
} from "@sagot/constants";
import { httpError, transportError } from "./errors";
import { authResponseSchema, hasAccessToken, type ParsedAuthResponse } from "./schemas";
import { toAccessToken, type AccessToken } from "./token";

/** Subset of `navigator.locks` we rely on (injectable for tests / old browsers). */
export interface LockManagerLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

/** Subset of `BroadcastChannel`. */
export interface ChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: MessageEvent) => void): void;
  close(): void;
}

/** "A refresh just happened" marker visible to every tab (epoch ms; never the token itself). */
export interface RefreshMarker {
  read(): number | null;
  write(at: number): void;
}

export type SessionStatus = "unknown" | "authenticated" | "anonymous";

export interface SessionSnapshot {
  status: SessionStatus;
  token: AccessToken | null;
  /** Impersonation tokens have no refresh cookie: they are never refreshed. */
  ephemeral: boolean;
}

type SessionMessage = { type: "token"; value: string; expiresAt: number } | { type: "signed-out" };

export interface SessionManagerOptions {
  realm: AuthRealm;
  baseUrl: string;
  fetch?: typeof fetch;
  locks?: LockManagerLike | null;
  channel?: ChannelLike | null;
  marker?: RefreshMarker | null;
  readCsrfCookie?: () => string | null;
  now?: () => number;
  /** How long a waiting tab trusts a sibling's broadcast to arrive before refreshing itself. */
  broadcastWaitMs?: number;
}

export interface SessionInput {
  access_token: string;
  expires_in?: number | null | undefined;
  csrf_token?: string | null | undefined;
}

const DEFAULT_BROADCAST_WAIT_MS = 1_000;

export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const match = document.cookie.split("; ").find((part) => part.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : null;
}

function defaultLocks(): LockManagerLike | null {
  if (typeof navigator === "undefined" || !("locks" in navigator)) return null;
  const locks = navigator.locks;
  return {
    request: async (name, callback) => {
      const result = await locks.request(name, callback);
      return result;
    },
  };
}

function defaultChannel(name: string): ChannelLike | null {
  return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(name);
}

function defaultMarker(key: string): RefreshMarker | null {
  if (typeof localStorage === "undefined") return null;
  return {
    read: () => {
      try {
        const raw = localStorage.getItem(key);
        return raw ? Number(raw) : null;
      } catch {
        return null;
      }
    },
    write: (at) => {
      try {
        localStorage.setItem(key, String(at));
      } catch {
        // Storage blocked: tabs still serialize through the lock; they just may rotate twice.
      }
    },
  };
}

const joinUrl = (base: string, path: string) => `${base.replace(/\/$/, "")}${path}`;

/**
 * In-memory access token + cookie-based refresh (DEC-020). Refresh is single-flight per tab and
 * serialized across tabs with the Web Locks API; the winner shares the new token over
 * BroadcastChannel so siblings do not rotate the refresh cookie again (rotation reuse revokes
 * the whole token family).
 */
export class SessionManager {
  private token: AccessToken | null = null;
  private csrf: string | null = null;
  private ephemeral = false;
  private status: SessionStatus = "unknown";
  private version = 0;
  private inflight: Promise<AccessToken | null> | null = null;
  private waiters = new Set<() => void>();
  private current: SessionSnapshot = { status: "unknown", token: null, ephemeral: false };
  private readonly listeners = new Set<(snapshot: SessionSnapshot) => void>();
  private readonly fetchImpl: typeof fetch;
  private readonly locks: LockManagerLike | null;
  private readonly channel: ChannelLike | null;
  private readonly marker: RefreshMarker | null;
  private readonly readCsrfCookie: () => string | null;
  private readonly now: () => number;
  private readonly broadcastWaitMs: number;

  constructor(private readonly options: SessionManagerOptions) {
    const scope = `${SESSION_SYNC.channelPrefix}-${options.realm}`;
    this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.locks = options.locks === undefined ? defaultLocks() : options.locks;
    this.channel = options.channel === undefined ? defaultChannel(scope) : options.channel;
    this.marker = options.marker === undefined ? defaultMarker(`${scope}-refreshed-at`) : options.marker;
    this.readCsrfCookie = options.readCsrfCookie ?? (() => readCookie(CSRF_COOKIE_NAME));
    this.now = options.now ?? Date.now;
    this.broadcastWaitMs = options.broadcastWaitMs ?? DEFAULT_BROADCAST_WAIT_MS;
    this.channel?.addEventListener("message", (event) => {
      this.onMessage(event.data as SessionMessage);
    });
  }

  /** Stable between changes (safe for `useSyncExternalStore`). */
  snapshot(): SessionSnapshot {
    return this.current;
  }

  subscribe(listener: (snapshot: SessionSnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getToken(): AccessToken | null {
    return this.token;
  }

  /** Adopts tokens from a sign-in response and shares them with sibling tabs. */
  setSession(input: SessionInput, options: { ephemeral?: boolean } = {}): AccessToken {
    const token = toAccessToken(input.access_token, input.expires_in, this.now());
    this.csrf = input.csrf_token ?? this.csrf;
    this.ephemeral = options.ephemeral ?? false;
    this.apply(token);
    if (!this.ephemeral) this.post({ type: "token", value: token.value, expiresAt: token.expiresAt });
    return token;
  }

  /** Forgets the session locally (and in sibling tabs unless `broadcast` is false). */
  clear(options: { broadcast?: boolean } = {}): void {
    this.token = null;
    this.ephemeral = false;
    this.status = "anonymous";
    this.version += 1;
    if (options.broadcast !== false) this.post({ type: "signed-out" });
    this.emit();
  }

  /** Valid token for the next request, refreshing first when it is about to expire. */
  async ensureFresh(): Promise<AccessToken | null> {
    const current = this.token;
    if (!current || this.ephemeral || !this.expiringSoon(current)) return current;
    try {
      return await this.refresh();
    } catch {
      return current;
    }
  }

  /** Silent restore on boot: one refresh attempt; resolves to the resulting status. */
  async restore(): Promise<SessionStatus> {
    if (this.status === "authenticated") return this.status;
    await this.refresh();
    return this.status;
  }

  refresh(): Promise<AccessToken | null> {
    if (this.ephemeral) {
      this.clear({ broadcast: false });
      return Promise.resolve(null);
    }
    if (this.inflight) return this.inflight;
    const startedAt = this.now();
    const startVersion = this.version;
    const run = () => this.refreshInsideLock(startedAt, startVersion);
    const name = `${SESSION_SYNC.lockPrefix}-${this.options.realm}`;
    this.inflight = (this.locks ? this.locks.request(name, run) : run()).finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /** Revokes the refresh cookie server-side, then signs every tab out. */
  async logout(): Promise<void> {
    const url = joinUrl(this.options.baseUrl, AUTH_ENDPOINTS[this.options.realm].logout);
    try {
      await this.fetchImpl(url, { method: "POST", credentials: "include", headers: this.csrfHeaders() });
    } catch {
      // Offline logout still clears the in-memory token; the cookie expires on its own.
    }
    this.clear();
  }

  private async refreshInsideLock(startedAt: number, startVersion: number): Promise<AccessToken | null> {
    if (this.version !== startVersion && this.token && !this.expiringSoon(this.token)) return this.token;
    const lastRefresh = this.marker?.read() ?? null;
    if (lastRefresh !== null && lastRefresh >= startedAt && (await this.awaitBroadcast(startVersion))) return this.token;
    return this.requestRefresh();
  }

  /** Waits briefly for a sibling's "token" message; true when one arrived. */
  private awaitBroadcast(startVersion: number): Promise<boolean> {
    if (this.version !== startVersion) return Promise.resolve(this.token !== null);
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        this.waiters.delete(done);
        resolve(this.version !== startVersion && this.token !== null);
      };
      const timer = setTimeout(done, this.broadcastWaitMs);
      this.waiters.add(done);
    });
  }

  private async requestRefresh(): Promise<AccessToken | null> {
    const url = joinUrl(this.options.baseUrl, AUTH_ENDPOINTS[this.options.realm].refresh);
    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: "POST",
        credentials: "include",
        headers: this.csrfHeaders(),
        signal: AbortSignal.timeout(DEFAULT_REQUEST_TIMEOUT_MS),
      });
    } catch {
      throw transportError("network");
    }
    if (response.status === 401 || response.status === 403) {
      this.clear();
      return null;
    }
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      throw httpError(response.status, body, {
        requestId: response.headers.get(API_HEADERS.requestId) ?? undefined,
        retryAfter: response.headers.get("Retry-After"),
      });
    }
    return this.adoptRefresh(body);
  }

  private adoptRefresh(body: unknown): AccessToken | null {
    const parsed = authResponseSchema.safeParse(body);
    if (!parsed.success || !hasAccessToken(parsed.data)) {
      this.clear();
      return null;
    }
    const token = this.setSession(parsed.data satisfies ParsedAuthResponse);
    this.marker?.write(this.now());
    return token;
  }

  private csrfHeaders(): Record<string, string> {
    const value = this.readCsrfCookie() ?? this.csrf;
    return value ? { [API_HEADERS.csrf]: value } : {};
  }

  private expiringSoon(token: AccessToken): boolean {
    return token.expiresAt - this.now() <= ACCESS_TOKEN_REFRESH_SKEW_MS;
  }

  private onMessage(message: SessionMessage): void {
    if (message.type === "signed-out") {
      if (!this.ephemeral) this.clear({ broadcast: false });
      return;
    }
    if (this.ephemeral) return;
    this.apply({ ...toAccessToken(message.value, null, this.now()), expiresAt: message.expiresAt });
  }

  private apply(token: AccessToken): void {
    this.token = token;
    this.status = "authenticated";
    this.version += 1;
    for (const wake of [...this.waiters]) wake();
    this.emit();
  }

  private post(message: SessionMessage): void {
    this.channel?.postMessage(message);
  }

  private emit(): void {
    this.current = { status: this.status, token: this.token, ephemeral: this.ephemeral };
    for (const listener of this.listeners) listener(this.current);
  }
}
